/**
 * AutoDiscoveryChip — subtle "NDOR is scoring for you today" signal.
 *
 * RC 1.25.H.1 — surfaces the new budget-bounded auto-score engine
 * on the Automation page so candidates can see the engine working
 * even when they haven't clicked a single thing today.
 *
 * Pure derivation from the existing `/api/automation/events` feed —
 * no new endpoint, no new table, no new migration, no localStorage.
 * Render rules:
 *   • Hidden unless mode === 'assisted_review'.
 *   • Shows today's scored count, today's queued count, daily budget.
 *   • Shows a zero-state copy when nothing's happened yet today.
 *
 * The pure stats function is exported separately so it can be unit-
 * tested without mounting React.
 */

import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Compute today's auto-score and queued counts from an event feed.
 *
 * @param {Array} events  Rows from GET /api/automation/events (newest-first).
 * @param {Date}  now     Clock override for tests. Defaults to new Date().
 * @returns {{scored:number, queued:number}}
 *
 * `scored`  — events.kind === 'auto_scored' created since 00:00 local today.
 * `queued`  — events.kind === 'queued' created since 00:00 local today.
 *             Every queue admission runs through the scanner + writes
 *             a `queued` event with `job_id`, so this is a faithful
 *             "how many roles reached your queue today" count. We
 *             deliberately don't try to filter to platform-only — the
 *             scanner's queue is already the user's review surface,
 *             and both user_import and platform additions are
 *             legitimate daily signals.
 */
export function computeAutoDiscoveryStats(events, now = new Date()) {
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const startMs = startOfDay.getTime();

  let scored = 0;
  let queued = 0;
  for (const e of events || []) {
    const t = new Date(e?.created_at || 0).getTime();
    if (!Number.isFinite(t) || t < startMs) continue;
    if (e.kind === 'auto_scored') scored += 1;
    else if (e.kind === 'queued')  queued += 1;
  }
  return { scored, queued };
}

/**
 * Chip component. Stateless — takes the already-loaded events list
 * from AutomationPage, the active mode, and the user's daily budget.
 */
export default function AutoDiscoveryChip({
  mode,
  events,
  dailyBudget,
}) {
  // Only visible in Assisted Review — the only mode that actually
  // auto-scores against the Source CV in batches. Manual never writes
  // auto_scored events. Automation runs continuously without a per-day
  // budget and uses the Top Opportunities feed instead of this chip.
  if (mode !== 'assisted_review') return null;

  const { scored, queued } = computeAutoDiscoveryStats(events);
  const budget = Number.isFinite(dailyBudget) ? dailyBudget : 10;
  const zeroState = scored === 0 && queued === 0;

  return (
    <div
      className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/40 px-4 py-3 flex items-center gap-3"
      data-testid="automation-auto-discovery-chip"
    >
      <Sparkles
        className="h-4 w-4 text-ndor-gold shrink-0"
        aria-hidden
      />
      <p className="text-sm text-ndor-ink-secondary font-manrope leading-snug">
        {zeroState ? (
          <span data-testid="automation-auto-discovery-chip-zero">
            NDORFlow has not run Analyse Fit on any new roles yet today.
          </span>
        ) : (
          <span data-testid="automation-auto-discovery-chip-stats">
            NDORFlow ran Analyse Fit against your Source CV on{' '}
            <span
              className="font-mono-ui text-ndor-ink tabular-nums"
              data-testid="automation-auto-discovery-chip-scored"
            >
              {scored}
            </span>
            {' / '}
            <span
              className="font-mono-ui text-ndor-ink tabular-nums"
              data-testid="automation-auto-discovery-chip-budget"
            >
              {budget}
            </span>
            {' '}new roles today ·{' '}
            <span
              className="font-mono-ui text-ndor-gold tabular-nums"
              data-testid="automation-auto-discovery-chip-queued"
            >
              {queued}
            </span>
            {' '}reached your Review Queue
          </span>
        )}
      </p>
    </div>
  );
}
